import { useState } from "react";

import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { PrimCollapsible } from "@/shared-web.ui/prim.collapsible";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { Separator } from "@/shared-web.ui/ui.separator";
import { cn } from "@/shared-web.ui/util.cn";
import {
  type SubscriptionCoffee,
} from "@/stf.data/schema";

interface CoffeeAttributeProps {
  name: string;
  value?: null | string | undefined;
}
const CoffeeAttribute = (props: CoffeeAttributeProps) => {
  const { name, value } = props;

  if (!value) {
    return null;
  }

  return (
    <li className="text-2">
      <span className="font-light">
        {`${name}: `}
      </span>
      <span>{value}</span>
    </li>
  );
};

interface SubsCoffeeCardProps extends SubscriptionCoffee {
  className?: string;
  collapseButtonColor?: "brown" | "gray";
  defaultOpen?: boolean;
  showWeight?: boolean;
}
const SubsCoffeeCard = (p: SubsCoffeeCardProps) => {
  const {
    showWeight = true,
    defaultOpen = false,
    className,
    collapseButtonColor = "brown",
  } = p;

  const [open, setOpen] = useState(defaultOpen);

  return (
    <PrimCollapsible.Root
      open={open}
      onOpenChange={setOpen}
    >
      <div className={cn(
        `bg-panel rounded-3 p-2 shadow-3`,
        className,
      )}
      >
        <div className="flex flex-col">
          <div className="flex flex-row gap-2">
            <div className="flex grow flex-col">
              <span className="font-medium">
                {p.coffeeName}
              </span>
              <Separator className="my-2" />
              <ul>
                <CoffeeAttribute
                  name="Roasters"
                  value={p.roasters}
                />
                <CoffeeAttribute
                  name="Roast Level"
                  value={p.roastLevel}
                />
              </ul>
            </div>
            {showWeight && p.weight
              ? (
                  <span>
                    {`${p.weight}g`}
                  </span>
                )
              : null}
            <PrimCollapsible.Trigger asChild>
              <IconButton
                screenReaders="Toggle details"
                variant="inline-ghost"
                color={collapseButtonColor}
              >
                {open
                  ? <ChevronUpIcon />
                  : <ChevronDownIcon />}
              </IconButton>
            </PrimCollapsible.Trigger>
          </div>
        </div>
        <PrimCollapsible.Content asChild>
          <div className="flex flex-col gap-2 pt-2">
            <Separator size="1" />
            <ul>
              <CoffeeAttribute
                name="Type"
                value={p.coffeeType}
              />
              <CoffeeAttribute
                name="Process"
                value={p.process}
              />
              <CoffeeAttribute
                name="Origin"
                value={p.origin}
              />
              <CoffeeAttribute
                name="Farm"
                value={p.farm}
              />
              <CoffeeAttribute
                name="Varietal"
                value={p.varietal}
              />
            </ul>
            <Separator size="1" />
            <div className="flex flex-col text-2">
              <span className="font-light">Taste Notes:</span>
              <span>{p.tasteNotes}</span>
            </div>
          </div>
        </PrimCollapsible.Content>
      </div>
    </PrimCollapsible.Root>
  );
};

export { SubsCoffeeCard };
